<script setup lang="ts">
import * as _ from 'lodash';
import {
  computed,
  onMounted,
  reactive,
  toRaw,
  watch,
  ref,
  onUnmounted,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet-draw';
import * as shapeUtils from './utils/shape';
import * as drawUtils from './utils/draw';
import * as markerUtils from '@/components/device/map/utils/marker.ts';
import * as layerUtils from '@/utils/map/layer';
import { reverseCoords } from '@/utils/coords';
import { clusterOptions } from '@/components/device/map/utils/marker.ts';
import { mapCenter } from '@/config/constants';
import { isAndroid } from '@/utils/user-agent';
import { useDevice } from '@/composables/useDevice';
import { usePosition } from '@/composables/usePosition.ts';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { useMapResize } from '@/utils/map/resize';
import { useAiActions } from '@/utils/map/aiActions.ts';
import { usePlaceSuggestionsMapLayer } from '@/composables/usePlaceSuggestionsMapLayer';
import { getAccountGeofence, getAccountMapOverlays } from '@gpx-spa/queries';
import {
  addOverlayIfEnabled,
  createOverlayPane,
  renderMapOverlays,
} from '@/utils/map/overlay';

const store = useStore();
const { getPositionByDeviceId } = usePosition();
const deviceStore = useDevice();
const { devices } = storeToRefs(deviceStore);

const route = useRoute();
const router = useRouter();

const props = defineProps<{
  view: string;
}>();

const data = reactive({
  dbClickTimer: null,
  center: mapCenter,
  map: null,
  mainLayers: [],
  layerControl: null,
  shape: null,
  shapes: L.featureGroup(),
  drawnItems: L.featureGroup(),
  drawControl: null,
  markers: null,
});

// Draw place-suggestion pins/polygons from the suggestions panel onto the map.
// Auto fit-bounds only on the cluster (list) view so detail/create/edit views
// are not yanked away from the geofence in focus.
usePlaceSuggestionsMapLayer(() => (data.map ? toRaw(data.map) : null), {
  shouldFitAll: () => props.view === 'cluster',
});

const canUseBleFeature = computed(() => store.getters['auth/canUseBleFeature']);
const viewportObj = computed(() => store.getters['geofence/viewportObj']);
const geofences = computed(() => store.getters['geofence/filtered']);
const seededShape = computed(() => store.getters['geofence/shape']);
let renderedSeedGeoJson: string | null = null;
let renderedSeedLayer: L.Polygon | null = null;

// Use vue-query for single geofence when in 'single' view
// For 'single' view, use vue-query composable; for other views, fallback to Vuex
const accountId = computed(() => store.getters['auth/accountId']);
const geofenceId = computed(() => +route.params.id);

// Only fetch via vue-query when we have both accountId and geofenceId
// This is for the 'single' view
const { data: geofenceData } = getAccountGeofence(accountId, geofenceId);

// Computed geofence: use vue-query data when available, fallback to Vuex
const geofence = computed(() => {
  // For 'single' view, prefer vue-query data
  if (props.view === 'single' && geofenceData.value) {
    return geofenceData.value;
  }

  // Fallback to Vuex for backward compatibility
  return store.getters['geofence/byId'](geofenceId.value);
});

const mapOverlayGroup = L.layerGroup([]);
const { data: mapOverlaysData } = getAccountMapOverlays(accountId);

let updateGeofencesDebounce = null;

const mapContainer = ref(null);
let cleanupResizeObserver: (() => void) | null = null;
const unsubscribeFromAiActions = ref(null);

onMounted(() => {
  if (!mapContainer.value) {
    return;
  }
  setupLeafletMap();
  setupCluster();
  setActiveLayer(props.view);
  updateGeofencesDebounce = _.debounce(updateGeofences, 200);

  const mapRef = computed(() => data.map);
  cleanupResizeObserver = useMapResize(mapRef, mapContainer, { delay: 0 });

  nextTick(() => {
    unsubscribeFromAiActions.value = useAiActions(mapRef);
  });
});

onUnmounted(() => {
  if (cleanupResizeObserver) {
    cleanupResizeObserver();
    cleanupResizeObserver = null;
  }

  if (unsubscribeFromAiActions.value) {
    unsubscribeFromAiActions.value();
    unsubscribeFromAiActions.value = null;
  }
});

watch(
  () => props.view,
  (newView, oldView) => {
    setActiveLayer(newView, oldView);
  },
);

watch(
  () => viewportObj.value,
  (n) => {
    if (['create', 'edit'].includes(props.view) && n) {
      const ne = n.getNorthEast();
      const sw = n.getSouthWest();
      const bounds = L.latLngBounds(
        L.latLng(ne.lat(), ne.lng()),
        L.latLng(sw.lat(), sw.lng()),
      );
      if (isAndroid()) {
        toRaw(data.map).fitBounds(bounds);
      } else {
        toRaw(data.map).flyToBounds(bounds, { duration: 1 });
      }
    }
  },
);

watch(
  () => geofences.value,
  (n, o) => {
    if (_.isEqual(n, o)) return;
    if (props.view === 'cluster') {
      updateGeofencesDebounce();
    }
  },
);

watch(
  [() => data.map, () => mapOverlaysData.value],
  ([mapInstance, overlaysData]) => {
    if (!mapInstance) return;
    renderMapOverlays(toRaw(mapInstance), overlaysData || [], mapOverlayGroup);
  },
  { immediate: true },
);

// Watch for geofence changes in 'single' view to update the map
watch(
  () => geofence.value,
  (newGeofence, oldGeofence) => {
    // Only handle updates if we're in 'single' view and map is initialized
    if (props.view !== 'single' || !data.map) {
      return;
    }

    // If geofence data just loaded (initial load)
    if (newGeofence && !oldGeofence) {
      // Create shape for the first time
      setupSingle();
      if (data.shape) {
        toRaw(data.map).addLayer(toRaw(data.shape));
        setSingleView();
      }
      return;
    }

    // Update existing shape if geofence data changed
    if (
      newGeofence &&
      oldGeofence &&
      data.shape &&
      !_.isEqual(newGeofence, oldGeofence)
    ) {
      // Remove old shape
      toRaw(data.map).removeLayer(toRaw(data.shape));

      // Create new shape with updated data
      createSingleShape(newGeofence);

      // Add updated shape to map
      toRaw(data.map).addLayer(toRaw(data.shape));

      // Update tooltip with new content
      const content = shapeUtils.getTooltipContent({ geofence: newGeofence });
      const shapeLayer = toRaw(data.shape);
      // Rebind tooltip with updated content
      if (shapeLayer.getTooltip()) {
        shapeLayer.unbindTooltip();
      }
      shapeLayer.bindTooltip(content, {
        sticky: true,
        opacity: 0.7,
      });
    }
  },
  { deep: true },
);

const setupLeafletMap = () => {
  const mainLayers = layerUtils.getLayers();
  const { id } = layerUtils.useLocalStorageLayer();
  data.mainLayers = mainLayers;

  if (!mapContainer.value) {
    return;
  }
  data.map = L.map(mapContainer.value, {
    layers: [toRaw(data.mainLayers)[id]],
    center: toRaw(data.center),
    zoomControl: false,
    zoom: 5,
    minZoom: 1,
  });

  L.control.zoom({ position: 'topright' }).addTo(toRaw(data.map));
  createOverlayPane(toRaw(data.map));

  const overlayLayers = {
    'Show overlays': mapOverlayGroup,
  };

  data.layerControl = L.control
    .layers(toRaw(data.mainLayers), overlayLayers, { position: 'topleft' })
    .addTo(toRaw(data.map));

  addOverlayIfEnabled(toRaw(data.map), mapOverlayGroup, 'mapOverlays');

  listenLayerChange();
  listenDraw();
  listenOverlayChange();
};

const setActiveLayer = (newView, oldView?) => {
  if (newView === 'cluster') {
    if (data.markers) {
      toRaw(data.markers).clearLayers();
      toRaw(data.map).removeLayer(toRaw(data.markers));
    }
    if (oldView === 'single') {
      toRaw(data.map).removeLayer(toRaw(data.shape));
      setupCluster();
      return setClusterView();
      // return data.setClusterViewWithFly();
    }
    if (oldView === 'create') {
      toRaw(data.map).removeLayer(toRaw(data.drawnItems));
      toRaw(data.map).removeControl(toRaw(data.drawControl));
      toRaw(data.drawnItems).clearLayers();
      return setClusterView();
      // return data.setClusterViewWithFly();
    }
    if (oldView === 'edit') {
      toRaw(data.shapes).clearLayers();
      toRaw(data.map).removeLayer(toRaw(data.shapes));
      setupCluster();
      toRaw(data.drawnItems).clearLayers();
      toRaw(data.map).removeLayer(toRaw(data.drawnItems));
      toRaw(data.map).removeControl(toRaw(data.drawControl));
      return setClusterView();
    }

    setClusterView();
  }
  if (newView === 'single') {
    setupSingle();
    // Only proceed if shape was created (geofence data exists)
    if (!data.shape) {
      return;
    }
    toRaw(data.map).removeLayer(toRaw(data.shapes));
    toRaw(data.shapes).clearLayers();
    toRaw(data.map).addLayer(toRaw(data.shape));

    if (oldView === 'create' || oldView === 'edit') {
      toRaw(data.shapes).clearLayers();
      toRaw(data.map).removeLayer(toRaw(data.shapes));
      toRaw(data.drawnItems).clearLayers();
      toRaw(data.map).removeLayer(toRaw(data.drawnItems));
      toRaw(data.map).removeControl(toRaw(data.drawControl));
      toRaw(data.markers).clearLayers();
      toRaw(data.map).removeLayer(toRaw(data.markers));
      setupDevicesCluster();
      setDevicesClusterView();
      setSingleView(data.shape);
      return;
    }

    setupDevicesCluster();
    setDevicesClusterView();
    setSingleView(data.shape);
  }
  if (newView === 'create') {
    setupDraw();
    setupDevicesCluster();
    setDevicesClusterView();
    if (oldView !== 'cluster') {
      if (oldView === 'single') {
        setupCluster();
      }
      setCreateView();
    }
    renderSeededShapeIfPresent();
  }
  if (newView === 'edit') {
    setupDevicesCluster();
    setDevicesClusterView();
    setupSingle();
    // Only proceed if shape was created (geofence data exists)
    if (!data.shape) {
      return;
    }
    toRaw(data.map).removeLayer(toRaw(data.shapes));
    toRaw(data.shapes).clearLayers();
    toRaw(data.map).addLayer(toRaw(data.shape));
    setSingleView();

    const shape = shapeUtils.convertLayerToShape(data.shape);
    setShape(shape);
    toRaw(data.map).removeLayer(toRaw(data.shape));
    toRaw(data.drawnItems).addLayer(toRaw(data.shape));
    setupDraw();
  }
};

const setupCluster = () => {
  geofences.value.forEach((geofence) => createClusterShape(geofence));
};

const setupSingle = () => {
  // Only create shape if geofence data is available
  if (!geofence.value) {
    return;
  }
  createSingleShape(geofence.value);
};
const seededGeometryToLatLngs = (geometry) => {
  if (geometry?.type === 'Polygon') {
    return geometry.coordinates.map((ring) => ring.map(reverseCoords));
  }
  if (geometry?.type === 'MultiPolygon') {
    return geometry.coordinates.map((poly) =>
      poly.map((ring) => ring.map(reverseCoords)),
    );
  }
  return null;
};

const renderSeededShapeIfPresent = () => {
  if (props.view !== 'create') return;
  if (!data.map) return;
  const seed = seededShape.value;
  if (!seed || seed.is_irregular !== 1 || !seed.geo_json) return;
  if (seed.geo_json === renderedSeedGeoJson) return;

  let geometry;
  try {
    geometry = JSON.parse(seed.geo_json);
  } catch {
    return;
  }
  const latLngs = seededGeometryToLatLngs(geometry);
  if (!latLngs) return;

  const drawnItems = toRaw(data.drawnItems);
  if (renderedSeedLayer) {
    drawnItems.removeLayer(renderedSeedLayer);
    renderedSeedLayer = null;
  }

  const layer = L.polygon(latLngs, {
    color: '#F25A54',
    weight: 2,
    fillOpacity: 0.2,
  });
  drawnItems.addLayer(layer);
  toRaw(data.map).fitBounds(layer.getBounds(), { maxZoom: 18 });
  renderedSeedLayer = layer;
  renderedSeedGeoJson = seed.geo_json;
};

watch(seededShape, () => renderSeededShapeIfPresent(), {
  immediate: true,
  flush: 'post',
});

const setupDraw = () => {
  toRaw(data.map).addLayer(toRaw(data.drawnItems));
  data.drawControl = new L.Control.Draw({
    position: 'topright',
    draw: drawUtils.getDrawOptions(),
    edit: {
      featureGroup: toRaw(data.drawnItems),
    },
  });
  toRaw(data.map).addControl(toRaw(data.drawControl));
};

const setupDevicesCluster = () => {
  data.markers = L.markerClusterGroup(clusterOptions);
  devices.value.forEach((device) => createClusterMarker(device));
};

const setClusterView = () => {
  if (!geofences.value.length) return;
  toRaw(data.map).addLayer(toRaw(data.shapes));
  if (route?.query) {
    const { lat, lng } = route.query;
    if (lat && lng) {
      const { zoom } = layerUtils.useLocalStorageZoom();
      toRaw(data.map).setView([lat, lng], zoom);
      return;
    }
  }
  const bounds = toRaw(data.shapes).getBounds();
  toRaw(data.map).fitBounds(bounds);
};

const setCreateView = () => {
  if (geofences.value.length) {
    toRaw(data.map).addLayer(toRaw(data.shapes));
  }
  const lat = route?.query?.lat;
  const lng = route?.query?.lng;
  if (lat && lng) {
    const { zoom } = layerUtils.useLocalStorageZoom();
    toRaw(data.map).setView([lat, lng], zoom);
  }
};

const setSingleView = () => {
  const bounds = toRaw(data.shape).getBounds();
  toRaw(data.map).fitBounds(bounds);
};

const setDevicesClusterView = () => {
  toRaw(data.map).addLayer(toRaw(data.markers));
};

const createClusterShape = (geofence) => {
  let popupHovered = false;
  let shapeHovered = false;

  const shape = shapeUtils.createShape(geofence);
  shape
    .bindPopup('', {
      closeButton: true,
      keepInView: true,
      minWidth: 50,
      autoPan: false,
    })
    .on('mouseover', function () {
      shapeHovered = true;
      const content = shapeUtils.getPopupContent({ geofence });
      this.getPopup().setContent(content);
      this.openPopup();
    })
    .on('mouseout', function () {
      shapeHovered = false;
      setTimeout(() => popupHovered || this.closePopup(), 100);
    })
    .on('click', () => {
      if (data.dbClickTimer !== null) {
        return;
      }
      data.dbClickTimer = setTimeout(() => {
        data.dbClickTimer = null;
        handleClusterShapeClick(geofence);
      }, 200);
    })
    .on('dblclick', () => {
      clearTimeout(data.dbClickTimer);
      data.dbClickTimer = null;
    })
    .on('popupopen', function (e) {
      const el = e.popup.getElement();
      el.addEventListener('mouseover', () => (popupHovered = true));
      el.addEventListener('mouseleave', () => {
        popupHovered = false;
        setTimeout(() => shapeHovered || e.popup.close(), 100);
      });
    });

  shape.addTo(toRaw(data.shapes));
};

const createSingleShape = (geofence) => {
  const content = shapeUtils.getTooltipContent({ geofence });
  data.shape = shapeUtils.createShape(geofence);
  toRaw(data.shape).bindTooltip(content, {
    sticky: true,
    opacity: 0.7,
  });
};

const createClusterMarker = async (device) => {
  const position = getPositionByDeviceId(device.id);
  if (!position) return;
  const content = markerUtils.getPopupContent(device, position);
  const marker = markerUtils.createMarker(device, position, {
    isNewIcon: canUseBleFeature,
  });

  marker
    .bindPopup(content, {
      closeButton: false,
      minWidth: 200,
    })
    .on('mouseover', function () {
      const content = markerUtils.getPopupContent(device, position);
      this.getPopup().setContent(content);
      this.openPopup();
    })
    .on('click', (event) => {
      if (data.dbClickTimer !== null) {
        return;
      }
      data.dbClickTimer = setTimeout(() => {
        data.dbClickTimer = null;
        handleClusterMarkerClick({ event, device });
      }, 200);
    })
    .on('dblclick', () => {
      clearTimeout(data.dbClickTimer);
      data.dbClickTimer = null;
    });

  toRaw(data.markers).addLayer(marker);
};

const handleClusterShapeClick = (geofence) => {
  router.push({
    name: 'GeofenceDetailsView',
    params: {
      id: geofence.id,
    },
  });
  createSingleShape(geofence);
};

const handleClusterMarkerClick = ({ event }) => {
  router.push({
    name: 'DeviceDetailsView',
    params: {
      id: event.target.options.id,
    },
  });
};

const updateGeofences = () => {
  toRaw(data.shapes).clearLayers();
  toRaw(data.map).removeLayer(toRaw(data.shapes));
  setupCluster();
  toRaw(data.shapes).addTo(toRaw(data.map));
  setClusterView();
};

/*
 * Map Listeners
 */

const listenLayerChange = () => {
  const { set } = layerUtils.useLocalStorageLayer();
  toRaw(data.map).on('baselayerchange', (e) => {
    set(e.name);
  });
};

const listenDraw = () => {
  const { reset } = layerUtils.useLocalStorageZoom();
  toRaw(data.map)
    .on(L.Draw.Event.CREATED, (e) => {
      const layer = e.layer;
      toRaw(data.drawnItems).addLayer(layer);
      const shape = shapeUtils.convertLayerToShape(layer);
      setShape(shape);
      reset();
    })
    .on(L.Draw.Event.EDITED, (e) => {
      const layers = e.layers;
      layers.eachLayer((layer) => {
        const shape = shapeUtils.convertLayerToShape(layer);
        setShape(shape);
      });
      reset();
    })
    .on(L.Draw.Event.DRAWSTART, () => {
      toRaw(data.drawnItems).clearLayers();
      setShape(null);
    })
    .on(L.Draw.Event.DELETED, (e) => {
      const layers = e.layers;
      layers.eachLayer(() => {
        setShape(null);
      });
      reset();
    });
};

const listenOverlayChange = () => {
  const { set: setMapOverlaysValue } =
    layerUtils.useLocalStorageOverlay('mapOverlays');
  toRaw(data.map).on('overlayadd', (e) => {
    if (e.name === 'Show overlays') setMapOverlaysValue(true);
  });
  toRaw(data.map).on('overlayremove', (e) => {
    if (e.name === 'Show overlays') setMapOverlaysValue(false);
  });
};

const setShape = (shape) => {
  store.dispatch('geofence/setShape', shape);
};
</script>
<template>
  <div
    ref="mapContainer"
    data-testid="geofence-create-map"
    class="z-0 size-full bg-gpx_gray-700"
  />
</template>
